import { useEffect, useState, useCallback, useRef } from "react";
import "./Lightbox.css";

function Lightbox({ event, initialIndex = 0, onClose }) {
	const [index, setIndex] = useState(initialIndex);
	const photoCount = event.photos.length;
	const overlayRef = useRef(null);
	const closeRef = useRef(null);

	const prev = useCallback(() => {
		setIndex((i) => (i === 0 ? photoCount - 1 : i - 1));
	}, [photoCount]);

	const next = useCallback(() => {
		setIndex((i) => (i === photoCount - 1 ? 0 : i + 1));
	}, [photoCount]);

	useEffect(() => {
		const handleKey = (e) => {
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowLeft") prev();
			if (e.key === "ArrowRight") next();
			// Keep Tab cycling inside the dialog.
			if (e.key === "Tab") {
				const focusables =
					overlayRef.current?.querySelectorAll("button");
				if (!focusables?.length) return;
				const first = focusables[0];
				const last = focusables[focusables.length - 1];
				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [onClose, prev, next]);

	useEffect(() => {
		const original = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		// Move focus into the dialog; give it back to the trigger on close.
		const trigger = document.activeElement;
		closeRef.current?.focus();
		return () => {
			document.body.style.overflow = original;
			if (trigger instanceof HTMLElement) trigger.focus();
		};
	}, []);

	const photo = event.photos[index];

	return (
		<div
			ref={overlayRef}
			className="lightbox-overlay"
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-label={`${event.title} fotogalerij`}>
			<button
				ref={closeRef}
				type="button"
				className="lightbox-close"
				onClick={onClose}
				aria-label="Sluit galerij">
				<span aria-hidden="true">×</span>
			</button>

			<button
				type="button"
				className="lightbox-nav lightbox-nav--prev"
				onClick={(e) => {
					e.stopPropagation();
					prev();
				}}
				aria-label="Vorige foto">
				<span aria-hidden="true">←</span>
			</button>

			<div
				className="lightbox-stage"
				onClick={(e) => e.stopPropagation()}>
				<img
					key={index}
					src={photo.src}
					alt={photo.alt}
					className="lightbox-image"
				/>
			</div>

			<button
				type="button"
				className="lightbox-nav lightbox-nav--next"
				onClick={(e) => {
					e.stopPropagation();
					next();
				}}
				aria-label="Volgende foto">
				<span aria-hidden="true">→</span>
			</button>

			<div
				className="lightbox-info"
				onClick={(e) => e.stopPropagation()}>
				<span className="lightbox-info-title">{event.title}</span>
				<span className="lightbox-info-counter">
					{index + 1} / {photoCount}
				</span>
			</div>
		</div>
	);
}

export default Lightbox;
